import Vue from "vue";
import axios from "axios";
import store from "@/store/index.js";
import CryptoJS from "vue-cryptojs";

Vue.use(CryptoJS);

// ===========================
// AXIOS API CONFIG
// ===========================
const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_LUMEN;

const API_MSG_BASE_URL =
  process.env.VUE_APP_API_BASE_URL_GO || process.env.VUE_APP_API_GO;

const REQUEST_TIMEOUT_MS = 20000;

const createApi = (baseURL) => {
  return axios.create({
    baseURL,
    timeout: REQUEST_TIMEOUT_MS,
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

// ===========================
// API DEFINE
// ===========================
export const apiPortal = createApi(API_BASE_URL);
export const apiCore = createApi(API_BASE_URL);
export const apiDPA = createApi("https://api.digipac.id/");
export const apiMsg = createApi(API_MSG_BASE_URL);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isNetworkError = (error) =>
  !!error && !error.response && error.message === "Network Error";

// tokenGen is safe to retry: a failed attempt produced no usable token, so at
// worst an unused token is generated server-side. Other (state-changing)
// calls deliberately keep single-attempt behaviour.
export const postWithNetworkRetry = async (
  instance,
  url,
  data,
  config,
  maxRetries = 2,
) => {
  let lastError = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await instance.post(url, data, config);
    } catch (error) {
      lastError = error;
      if (!isNetworkError(error) || attempt === maxRetries) throw error;
      // Exponential backoff: fresh TLS connection per attempt helps against
      // the transient handshake/socket drops seen on iOS mobile networks.
      await sleep(500 * Math.pow(2, attempt));
    }
  }
  throw lastError;
};

export const accessApi = async () => {
  // Omit missing fields instead of sending explicit `null` so the JSON
  // payload is minimal and unambiguous for strict parsers/WAFs.
  const sendData = { msisdn: store.state.user };
  if (
    store.state.mno_id !== null &&
    store.state.mno_id !== undefined &&
    store.state.mno_id !== ""
  ) {
    sendData.mno_id = store.state.mno_id;
  }
  const headers = {
    Authorization: `bearer ${store.state.appKey}`,
  };
  try {
    const { data } = await postWithNetworkRetry(
      apiCore,
      "arcade/tokenGen",
      sendData,
      { headers },
    );
    return data.single_token;
  } catch (error) {
    // Enrich network-layer failures so remote logs can distinguish offline /
    // CORS-blocked / TLS-failed without needing the device in hand.
    if (isNetworkError(error)) {
      error.clientDiagnostics = {
        onLine: typeof navigator !== "undefined" ? navigator.onLine : undefined,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        url:
          error.config && error.config.baseURL
            ? error.config.baseURL + error.config.url
            : error.config && error.config.url,
      };
    }
    throw error;
  }
};

export const response = (payload) => {
  const res = payload;
  const result = atob(res.split("").reverse().join(""));
  return result;
};

export const responseApi = (payload) => {
  const res = payload;
  // console.log(res.payload, store.state.secretKey);
  var decr = toDecrypt(res.payload, store.state.secretKey);
  return decr;
};

export const toEncrypt = (string, key) => {
  var encryptMethod = "AES-256-CBC";
  var aesNumber = parseInt(encryptMethod.match(/\d+/)[0]);
  var iv = Vue.CryptoJS.lib.WordArray.random(16); // the reason to be 16, please read on `encryptMethod` property.

  var salt = Vue.CryptoJS.lib.WordArray.random(256);
  var iterations = 999;
  var encryptMethodLength = aesNumber / 4; // example: AES number is 256 / 4 = 64
  var hashKey = Vue.CryptoJS.PBKDF2(key, salt, {
    hasher: Vue.CryptoJS.algo.SHA512,
    keySize: encryptMethodLength / 8,
    iterations: iterations,
  });

  var encrypted = Vue.CryptoJS.AES.encrypt(string, hashKey, {
    mode: Vue.CryptoJS.mode.CBC,
    iv: iv,
  });
  var encryptedString = Vue.CryptoJS.enc.Base64.stringify(encrypted.ciphertext);

  var output = {
    value: encryptedString,
    iv: Vue.CryptoJS.enc.Hex.stringify(iv),
    salt: Vue.CryptoJS.enc.Hex.stringify(salt),
    iterations: iterations,
  };

  return Vue.CryptoJS.enc.Base64.stringify(
    Vue.CryptoJS.enc.Utf8.parse(JSON.stringify(output)),
  );
};

export const toDecrypt = (encryptedString, key) => {
  var encryptMethod = "AES-256-CBC";
  var aesNumber = parseInt(encryptMethod.match(/\d+/)[0]);
  var json = JSON.parse(
    Vue.CryptoJS.enc.Utf8.stringify(
      Vue.CryptoJS.enc.Base64.parse(encryptedString),
    ),
  );

  var salt = Vue.CryptoJS.enc.Hex.parse(json.salt);
  var iv = Vue.CryptoJS.enc.Hex.parse(json.iv);

  var encrypted = json.value; // no need to base64 decode.

  var iterations = parseInt(json.iterations);
  if (iterations <= 0) {
    iterations = 999;
  }
  var encryptMethodLength = aesNumber / 4; // example: AES number is 256 / 4 = 64
  var hashKey = Vue.CryptoJS.PBKDF2(key, salt, {
    hasher: Vue.CryptoJS.algo.SHA512,
    keySize: encryptMethodLength / 8,
    iterations: iterations,
  });

  var decrypted = Vue.CryptoJS.AES.decrypt(encrypted, hashKey, {
    mode: Vue.CryptoJS.mode.CBC,
    iv: iv,
  });
  // console.log(Vue.CryptoJS.enc.Utf8.parse(decrypted).toString());
  return decrypted.toString(Vue.CryptoJS.enc.Utf8);
};

// Allow ops to point the API at a same-origin proxy (e.g. "/api/v1/")
// without a code change. Same-origin removes CORS preflight entirely and
// is the most robust workaround for strict WebKit (iOS) CORS/TLS handling.
// const API_BASE_URL =
//   process.env.VUE_APP_API_BASE_URL || "https://coregame.ina17.com/api/v1/";
// const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
// export const apiPortal = createApi(
//   // You can add your headers here
//   // ================================
//   // baseURL: "http://localhost:8000/api/v1/"
//   // baseURL: 'https://stgcoregamecms.ina17.com/api/v1/',
//   API_BASE_URL,
//   // "https://xgoal.cellcard.com.kh/api/v1",
// );

// export const apiDPA = createApi(
//   // You can add your headers here
//   // ================================
//   // baseURL: "http://localhost:8000/api/v1/"
//   // baseURL: ' https://stagingapi.digipac.id/',
//   "https://api.digipac.id/",
// );

// export const apiCore = createApi(
//   // baseURL: "http://localhost:8000/api/v1/",
//   // baseURL: 'https://stgcoregamecms.ina17.com/api/v1/',
//   API_BASE_URL,
//   // "https://xgoal.cellcard.com.kh/api/v1",
// );

// // API MSG CELLCARD
// export const apiMsg = createApi(
//   "/go" + API_BASE_URL,
//   // "https://coregame.ina17.com/go/api/v1/",
//   // "https://xgoal.cellcard.com.kh/go/api/v1",
//   // "http://localhost:8181/go/api/v1/",
//   // baseURL: 'https://stgcoregamecms.ina17.com/api/v1/',
//   // baseURL: "https://coregame.ina17.com/go/api/v1/",
// );
